import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';
import ThemeToggle from '@/components/ThemeToggle';
import { BrandLockup } from '@/components/Brand';
import ClassificationBar from '@/components/chrome/ClassificationBar';
import IntelTicker from '@/components/chrome/IntelTicker';
import StatusStrip from '@/components/chrome/StatusStrip';
import '@/styles/welcome.css';

// In-page sections of the landing page (pages/welcome/*). Every link goes
// somewhere real; there are no placeholder pages behind them.
const NAV_ITEMS = [
  { label: 'Platform', id: 'platform' },
  { label: 'Workspace', id: 'workspace' },
  { label: 'Sources', id: 'sources' },
  { label: 'Security', id: 'security' },
  { label: 'How it works', id: 'how-it-works' },
];

function SiteHeader({ onLanding }) {
  const [open, setOpen] = useState(false);
  // On the landing page the anchors scroll in place; elsewhere they load it.
  const href = (id) => (onLanding ? `#${id}` : `/#${id}`);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header className="pub-header">
      <div className="pub-header-inner">
        <Link to="/" className="pub-brand" aria-label="IntelliSecure home">
          <BrandLockup size={26} />
        </Link>

        <nav className="pub-nav" aria-label="Main">
          {NAV_ITEMS.map((item) => (
            <a key={item.id} href={href(item.id)}>{item.label}</a>
          ))}
        </nav>

        <div className="pub-header-actions">
          <ThemeToggle />
          <Link to="/login" className="pub-signin">Sign in</Link>
          <Link to="/register" className="is-btn-primary pub-cta-short" style={{ padding: '8px 14px', textDecoration: 'none' }}>
            Request access
            <ArrowRight size={13} aria-hidden="true" />
          </Link>
          <button
            type="button"
            className="is-icon-btn pub-menu-btn"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="pub-mobile-nav"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </div>

      <nav
        id="pub-mobile-nav"
        className="pub-mobile-nav"
        data-open={open}
        aria-label="Main"
        hidden={!open}
      >
        {NAV_ITEMS.map((item) => (
          <a key={item.id} href={href(item.id)} onClick={() => setOpen(false)}>{item.label}</a>
        ))}
        <Link to="/login" className="pub-mobile-signin" onClick={() => setOpen(false)}>Sign in</Link>
      </nav>
    </header>
  );
}

function SiteFooter({ onLanding }) {
  const href = (id) => (onLanding ? `#${id}` : `/#${id}`);
  return (
    <footer className="pub-footer">
      <div className="pub-footer-inner">
        <div className="pub-footer-grid">
          <div>
            <BrandLockup size={24} />
            <p className="pub-footer-blurb">
              Threat intelligence and breach monitoring matched to your industry, region and
              security stack.
            </p>
          </div>
          <nav aria-labelledby="pub-footer-product">
            <h2 id="pub-footer-product" className="t-label pub-footer-heading">Product</h2>
            <ul className="pub-footer-list">
              {NAV_ITEMS.map((item) => (
                <li key={item.id}><a href={href(item.id)}>{item.label}</a></li>
              ))}
            </ul>
          </nav>
          <nav aria-labelledby="pub-footer-account">
            <h2 id="pub-footer-account" className="t-label pub-footer-heading">Account</h2>
            <ul className="pub-footer-list">
              <li><Link to="/login">Sign in</Link></li>
              <li><Link to="/register">Request access</Link></li>
            </ul>
          </nav>
        </div>
        <div className="pub-footer-bottom">© {new Date().getFullYear()} IntelliSecure</div>
      </div>
    </footer>
  );
}

/**
 * AuthShell: the outer chrome for unauthenticated pages. Styles for every
 * public page live in styles/welcome.css (imported here).
 *
 * variant="hero"  full-bleed landing page with header, ticker and footer.
 * variant="split" login / register: only the marking bar and status strip;
 *                 the child (AuthSplit) renders the rest.
 * variant="card"  centered column with the marketing header (404 page).
 *
 * panelVariant="admin" renders the staff sign-in: no marketing header and
 * no platform chrome, so nothing privileged is shown before sign-in.
 */
export default function AuthShell({ variant = 'card', panelVariant = 'customer', children, showTicker = true, showFooter = true }) {
  const isHero = variant === 'hero';
  const isSplit = variant === 'split';
  const isAdmin = panelVariant === 'admin';
  const showChrome = !isAdmin;
  const showHeader = !isSplit && !isAdmin;

  return (
    <div
      data-panel={panelVariant}
      className={`pub-shell${isSplit ? ' pub-shell--split' : ''}${isAdmin ? ' admin-panel-root' : ''}`}
    >
      <a href="#main" className="pub-skip">Skip to content</a>
      {showChrome && <ClassificationBar variant={panelVariant} />}

      {showHeader && <SiteHeader onLanding={isHero} />}

      {showTicker && isHero && <IntelTicker />}

      <main id="main" className={`pub-main${variant === 'card' && !isAdmin ? ' pub-main--center' : ''}`}>
        {children}
      </main>

      {showFooter && isHero && <SiteFooter onLanding />}

      {showChrome && <StatusStrip variant={panelVariant} />}
    </div>
  );
}
